import * as THREE from "three";
import * as CANNON from "cannon-es";
import { STLLoader } from "three/addons/loaders/STLLoader.js";
import { ThreeMFLoader } from "three/addons/loaders/3MFLoader.js";
import { listParts, partUrl, type PartInfo } from "./parts-api";
import { ToolPanel, type PanelButton } from "./tool-panel";

const PART_TARGET_SIZE = 0.25;
const GRAB_RANGE = 0.20;
const ROT_SENSITIVITY = Math.PI;
const STICK_DEADZONE = 0.15;

const COLOR_PALETTE: number[] = [
  0xc9c9d2, 0x4f46e5, 0xe11d48, 0xf59e0b,
  0x10b981, 0x06b6d4, 0x8b5cf6, 0x111118,
];
const DEFAULT_COLOR = COLOR_PALETTE[0];
const SWATCH_RADIUS = 0.025;
const SWATCH_SPACING = 0.07;
const SWATCH_HOVER_SCALE = 1.4;
const PICKER_OFFSET_Y = -0.25;
const DELETE_BTN_GAP = 0.05;
const DELETE_BTN_SIZE = SWATCH_RADIUS * 1.7;

const STORAGE_KEY = "metaapp.parts.v1";
const SAVE_DEBOUNCE_MS = 500;

const INFO_PANEL_OFFSET_Y = 0.30;
const PART_MASS = 0.4;

// Meta's persistent-anchor extension (not in @types/webxr yet).
type PersistableAnchor = XRAnchor & {
  requestPersistentHandle?(): Promise<string>;
  delete?(): void;
};
type AnchorCapableSession = XRSession & {
  restorePersistentAnchor?(handle: string): Promise<XRAnchor>;
  deletePersistentAnchor?(handle: string): Promise<void>;
};
type AnchorCapableFrame = XRFrame & {
  createAnchor?(transform: XRRigidTransform, space: XRSpace): Promise<XRAnchor>;
};

type PersistedPart = {
  filename: string;
  color: number;
  anchorHandle: string;
  rotation: [number, number, number, number]; // quaternion x,y,z,w
  physicsEnabled?: boolean;
};

type SwatchAction = "color" | "delete";
type SwatchHit = { part: PlacedPart; mesh: THREE.Mesh; action: SwatchAction };

type PlacedPart = {
  filename: string;
  color: number;
  object: THREE.Object3D;
  picker: THREE.Group;
  // Per-part info panel; lazily created on first thumbstick-click toggle.
  infoPanel: ToolPanel | null;
  anchor: XRAnchor | null;
  anchorHandle: string | null;
  needsAnchor: boolean;
  anchorPending: boolean;
  // Physics state (off by default; toggled per-part via the info panel).
  physicsEnabled: boolean;
  body: CANNON.Body | null;
  /**
   * Offset from the visible mesh's `position` to the AABB center in world
   * units (= localCenter * scale). Stored at body-creation time so we can
   * convert body.position back to mesh.position when syncing each frame:
   *   mesh.position = body.position - body.quaternion * bodyLocalOffset
   */
  bodyLocalOffset: THREE.Vector3 | null;
};

export type PartsToolHandle = {
  group: THREE.Group;
  update(
    dt: number,
    controllers: THREE.Object3D[],
    frame: XRFrame | null,
    refSpace: XRReferenceSpace | null,
  ): void;
  dispose(): void;
  onSelectStart(controller: THREE.Object3D): boolean;
  onSelectEnd(controller: THREE.Object3D): void;
  refresh(): Promise<void>;
  listHoverDistance(controller: THREE.Object3D): number | null;
  /**
   * Called when B/Y is pressed. If the tool has an overlay to dismiss (e.g.
   * the parts list while parts are already placed), it consumes the press
   * and returns true. Otherwise returns false and the caller should leave
   * the tool.
   */
  tryDismissOverlay(): boolean;
};

export async function startPartsTool(
  scene: THREE.Scene,
  camera: THREE.Camera,
  session: XRSession,
  world: CANNON.World,
  partMat: CANNON.Material,
  setStatus: (msg: string) => void,
): Promise<PartsToolHandle> {
  const group = new THREE.Group();
  scene.add(group);

  let listPanel: ToolPanel | null = null;
  const placedParts: PlacedPart[] = [];
  const grabbed = new Map<THREE.Object3D, PlacedPart>();
  // Joystick rotation acts on the most recently grabbed/loaded part.
  let lastFocused: PlacedPart | null = null;

  // Pickers are per-part. A single pickersVisible flag toggles all of them.
  let pickersVisible = false;
  const pickerHover = new Map<THREE.Object3D, SwatchHit | null>();
  const pickerRay = new THREE.Raycaster();
  const prevPickerToggle = new Map<XRInputSource, boolean>();
  // Grip button (squeeze, button[1]) toggles the parts list visibility.
  const prevGripToggle = new Map<XRInputSource, boolean>();
  // Thumbstick click (button[3]) — toggles the info panel of the part the
  // controller is currently pointing at.
  const prevStickClick = new Map<XRInputSource, boolean>();

  // Debounced save during rotation, since it would otherwise fire every frame
  // the user pushes the stick.
  let saveTimer: number | null = null;
  function scheduleSave(): void {
    if (saveTimer !== null) window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => { saveState(); saveTimer = null; }, SAVE_DEBOUNCE_MS);
  }

  const stlLoader = new STLLoader();
  const mfLoader = new ThreeMFLoader();

  // ── Parts list panel ────────────────────────────────────────────────
  async function showList(): Promise<void> {
    if (listPanel) {
      group.remove(listPanel.group);
      listPanel = null;
    }
    setStatus("Loading parts list…");
    let parts: PartInfo[];
    try {
      parts = await listParts();
    } catch (err) {
      setStatus(`Could not load parts: ${(err as Error).message}`);
      return;
    }
    if (parts.length === 0) {
      setStatus("No parts uploaded yet. Upload .stl/.3mf from the desktop page first.");
      return;
    }
    const buttons: PanelButton[] = parts.slice(0, 8).map((p) => ({
      label: trimLabel(p.name),
      onSelect: () => void loadAndPlace(p),
    }));
    listPanel = new ToolPanel("Add a part", buttons);
    listPanel.placeInFrontOf(camera, 1.0);
    group.add(listPanel.group);
    refreshStatus();
  }

  function refreshStatus(): void {
    const n = placedParts.length;
    if (n === 0) {
      setStatus(`Pinch a list button to add a part • B/Y back.`);
    } else {
      setStatus(`${n} placed. Pinch grab • sticks rotate • Click stick info • A/X colors • Grip list • B/Y back.`);
    }
  }

  function toggleList(): void {
    if (listPanel) {
      group.remove(listPanel.group);
      listPanel = null;
      refreshStatus();
    } else {
      void showList();
    }
  }

  function pollGripButton(): void {
    let pressedNow = false;
    for (const src of session.inputSources) {
      const gp = src.gamepad;
      // button[1] = squeeze / grip on Quest controllers
      if (!gp || gp.buttons.length < 2) continue;
      const isDown = gp.buttons[1]?.pressed ?? false;
      const wasDown = prevGripToggle.get(src) ?? false;
      if (isDown && !wasDown) pressedNow = true;
      prevGripToggle.set(src, isDown);
    }
    if (prevGripToggle.size > 4) {
      const live = new Set(session.inputSources);
      for (const k of [...prevGripToggle.keys()]) if (!live.has(k)) prevGripToggle.delete(k);
    }
    if (pressedNow) toggleList();
  }

  // ── Place / load ────────────────────────────────────────────────────
  async function loadAndPlace(
    p: PartInfo,
    opts: {
      restoreColor?: number;
      restoreAnchor?: XRAnchor | null;
      restoreHandle?: string | null;
      restoreRotation?: [number, number, number, number];
      restorePhysicsEnabled?: boolean;
    } = {},
  ): Promise<PlacedPart | null> {
    let obj: THREE.Object3D;
    try {
      obj = await loadPart(p.name, stlLoader, mfLoader);
    } catch (err) {
      setStatus(`Failed to load ${p.name}: ${(err as Error).message}`);
      return null;
    }

    autoScaleAndCenter(obj, PART_TARGET_SIZE);
    const color = opts.restoreColor ?? DEFAULT_COLOR;
    applyColor(obj, color);

    if (opts.restoreAnchor) {
      // Position will be set from anchor pose on next frame; rotation comes
      // from saved data immediately.
      if (opts.restoreRotation) {
        obj.quaternion.set(...opts.restoreRotation);
      }
    } else {
      placeNewPart(obj, placedParts.length);
    }
    group.add(obj);

    const picker = createPicker();
    picker.visible = pickersVisible;
    group.add(picker);

    const part: PlacedPart = {
      filename: p.name,
      color,
      object: obj,
      picker,
      infoPanel: null,
      anchor: opts.restoreAnchor ?? null,
      anchorHandle: opts.restoreHandle ?? null,
      needsAnchor: !opts.restoreAnchor,
      anchorPending: false,
      physicsEnabled: false,
      body: null,
      bodyLocalOffset: null,
    };
    placedParts.push(part);
    setLastFocused(part);
    if (opts.restorePhysicsEnabled) {
      // Defer physics enable to the next frame after the anchor pose has had
      // a chance to position the part correctly.
      setTimeout(() => { if (placedParts.includes(part)) enablePhysics(part); }, 100);
    }
    refreshStatus();
    return part;
  }

  function placeNewPart(obj: THREE.Object3D, slotIndex: number): void {
    const camPos = new THREE.Vector3();
    const camDir = new THREE.Vector3();
    const camRight = new THREE.Vector3(1, 0, 0).applyQuaternion(
      camera.getWorldQuaternion(new THREE.Quaternion()),
    );
    camera.getWorldPosition(camPos);
    camera.getWorldDirection(camDir);
    // Stagger horizontally so subsequent parts don't all land in the same spot.
    const lateral = ((slotIndex % 4) - 1.5) * 0.18;
    obj.position.copy(camPos)
      .add(camDir.multiplyScalar(0.6))
      .add(camRight.multiplyScalar(lateral));
    obj.position.y = Math.max(obj.position.y, 0.6);
  }

  // ── Per-part picker (color + delete) ────────────────────────────────
  function createPicker(): THREE.Group {
    const g = new THREE.Group();
    const totalW = (COLOR_PALETTE.length - 1) * SWATCH_SPACING;
    for (let i = 0; i < COLOR_PALETTE.length; i++) {
      const sphere = new THREE.Mesh(
        new THREE.SphereGeometry(SWATCH_RADIUS, 20, 14),
        new THREE.MeshStandardMaterial({
          color: COLOR_PALETTE[i], roughness: 0.45, metalness: 0.05,
        }),
      );
      sphere.position.x = i * SWATCH_SPACING - totalW / 2;
      sphere.userData.action = "color" as SwatchAction;
      sphere.userData.swatchColor = COLOR_PALETTE[i];
      g.add(sphere);
    }
    // Delete button — a red cube at the right end of the row, shape-distinct
    // from the color swatches so it can't be confused with the red color.
    const del = new THREE.Mesh(
      new THREE.BoxGeometry(DELETE_BTN_SIZE, DELETE_BTN_SIZE, DELETE_BTN_SIZE),
      new THREE.MeshStandardMaterial({ color: 0xdc2626, roughness: 0.4, metalness: 0.1 }),
    );
    del.position.x = totalW / 2 + DELETE_BTN_GAP + DELETE_BTN_SIZE / 2;
    del.userData.action = "delete" as SwatchAction;
    g.add(del);
    return g;
  }

  function pollPickerToggle(): void {
    if (placedParts.length === 0) return;
    let pressedNow = false;
    for (const src of session.inputSources) {
      const gp = src.gamepad;
      // button[4] = A on right / X on left
      if (!gp || gp.buttons.length < 5) continue;
      const isDown = gp.buttons[4]?.pressed ?? false;
      const wasDown = prevPickerToggle.get(src) ?? false;
      if (isDown && !wasDown) pressedNow = true;
      prevPickerToggle.set(src, isDown);
    }
    if (prevPickerToggle.size > 4) {
      const live = new Set(session.inputSources);
      for (const k of [...prevPickerToggle.keys()]) if (!live.has(k)) prevPickerToggle.delete(k);
    }
    if (!pressedNow) return;
    pickersVisible = !pickersVisible;
    if (!pickersVisible) {
      pickerHover.clear();
      for (const p of placedParts) {
        for (const c of p.picker.children) (c as THREE.Mesh).scale.setScalar(1);
      }
    }
    // Per-part visibility is computed each frame in updatePickerVisibility so
    // that "selected = last grabbed/loaded" stays current.
  }

  function updatePickerVisibility(): void {
    const focused = lastFocused;
    const focusedGrabbed = focused !== null && [...grabbed.values()].includes(focused);
    for (const p of placedParts) {
      const shouldShow = pickersVisible && p === focused && !focusedGrabbed;
      if (p.picker.visible !== shouldShow) {
        p.picker.visible = shouldShow;
        if (!shouldShow) {
          for (const c of p.picker.children) (c as THREE.Mesh).scale.setScalar(1);
        }
      }
    }
  }

  function setLastFocused(p: PlacedPart | null): void {
    if (lastFocused === p) return;
    // Clear hover state attached to the previous focused part's picker.
    pickerHover.clear();
    for (const part of placedParts) {
      for (const c of part.picker.children) (c as THREE.Mesh).scale.setScalar(1);
    }
    lastFocused = p;
  }

  function updatePickerPlacements(): void {
    for (const p of placedParts) {
      if (!p.picker.visible) continue;
      const partWorld = new THREE.Vector3();
      p.object.getWorldPosition(partWorld);
      // Stay axis-aligned in world: the row of swatches stays horizontal even
      // if the part is rotated, so it's always readable.
      p.picker.position.set(partWorld.x, partWorld.y + PICKER_OFFSET_Y, partWorld.z);
    }
  }

  function updatePickerHover(controllers: THREE.Object3D[]): void {
    if (!pickersVisible) return;
    const meshes: THREE.Mesh[] = [];
    const meshToPart = new Map<THREE.Mesh, PlacedPart>();
    for (const p of placedParts) {
      // Three.js Raycaster doesn't auto-skip invisible objects, so we filter
      // here. With single-part selection only the focused part's picker is
      // visible, so this restricts hover to that one.
      if (!p.picker.visible) continue;
      for (const child of p.picker.children) {
        const mesh = child as THREE.Mesh;
        meshes.push(mesh);
        meshToPart.set(mesh, p);
      }
    }
    for (const ctrl of controllers) {
      const origin = new THREE.Vector3();
      const dir = new THREE.Vector3();
      ctrl.getWorldPosition(origin);
      dir.set(0, 0, -1).applyQuaternion(ctrl.getWorldQuaternion(new THREE.Quaternion()));
      pickerRay.set(origin, dir);
      const hits = pickerRay.intersectObjects(meshes, false);
      const newHit: SwatchHit | null = hits.length > 0
        ? {
            part: meshToPart.get(hits[0].object as THREE.Mesh)!,
            mesh: hits[0].object as THREE.Mesh,
            action: hits[0].object.userData.action as SwatchAction,
          }
        : null;
      const old = pickerHover.get(ctrl) ?? null;
      if (newHit?.mesh !== old?.mesh) {
        if (old && !anotherCtrlStillHovers(old.mesh, ctrl)) old.mesh.scale.setScalar(1);
        if (newHit) newHit.mesh.scale.setScalar(SWATCH_HOVER_SCALE);
        pickerHover.set(ctrl, newHit);
      }
    }
  }

  function anotherCtrlStillHovers(mesh: THREE.Mesh, except: THREE.Object3D): boolean {
    for (const [ctrl, hov] of pickerHover) {
      if (ctrl !== except && hov?.mesh === mesh) return true;
    }
    return false;
  }

  function pickerHoverDistance(controller: THREE.Object3D): number | null {
    const hit = pickerHover.get(controller);
    if (!hit) return null;
    const a = new THREE.Vector3(); const b = new THREE.Vector3();
    controller.getWorldPosition(a);
    hit.mesh.getWorldPosition(b);
    return a.distanceTo(b);
  }

  function tryPickerAction(controller: THREE.Object3D): boolean {
    const hit = pickerHover.get(controller);
    if (!hit) return false;
    if (hit.action === "color") {
      const color = hit.mesh.userData.swatchColor as number;
      hit.part.color = color;
      applyColor(hit.part.object, color);
      saveState();
      return true;
    }
    if (hit.action === "delete") {
      void deletePart(hit.part);
      return true;
    }
    return false;
  }

  // ── Per-part info panel ─────────────────────────────────────────────
  function ensureInfoPanel(part: PlacedPart): ToolPanel {
    if (part.infoPanel) return part.infoPanel;
    const panel = new ToolPanel(part.filename, [
      {
        label: part.physicsEnabled ? "Disable Physics" : "Enable Physics",
        onSelect: () => togglePhysics(part),
      },
    ]);
    panel.group.visible = false;
    group.add(panel.group);
    part.infoPanel = panel;
    return panel;
  }

  function toggleInfoPanel(part: PlacedPart): void {
    const panel = ensureInfoPanel(part);
    panel.group.visible = !panel.group.visible;
    if (panel.group.visible) placeInfoPanel(part);
    setLastFocused(part);
  }

  function placeInfoPanel(part: PlacedPart): void {
    if (!part.infoPanel) return;
    // Position above the part. lookAt the camera so the user always reads it
    // squarely regardless of the part's rotation or the user's position.
    const partWorld = new THREE.Vector3();
    part.object.getWorldPosition(partWorld);
    part.infoPanel.group.position.set(
      partWorld.x,
      partWorld.y + INFO_PANEL_OFFSET_Y,
      partWorld.z,
    );
    const camPos = new THREE.Vector3();
    camera.getWorldPosition(camPos);
    part.infoPanel.group.lookAt(camPos);
  }

  function updateInfoPanels(controllers: THREE.Object3D[]): void {
    for (const p of placedParts) {
      if (!p.infoPanel || !p.infoPanel.group.visible) continue;
      placeInfoPanel(p);
      for (const ctrl of controllers) p.infoPanel.updateHover(ctrl);
    }
  }

  function infoPanelHoverDistance(controller: THREE.Object3D): number | null {
    let best: number | null = null;
    for (const p of placedParts) {
      if (!p.infoPanel || !p.infoPanel.group.visible) continue;
      const d = p.infoPanel.hoverDistance(controller);
      if (d !== null && (best === null || d < best)) best = d;
    }
    return best;
  }

  function tryInfoPanelClick(controller: THREE.Object3D): boolean {
    for (const p of placedParts) {
      if (!p.infoPanel || !p.infoPanel.group.visible) continue;
      const button = p.infoPanel.updateHover(controller);
      if (button) {
        button.onSelect();
        return true;
      }
    }
    return false;
  }

  // ── Pointing-at-part raycast (used for thumbstick-click toggle) ─────
  function findPointedPart(controller: THREE.Object3D): PlacedPart | null {
    if (placedParts.length === 0) return null;
    const origin = new THREE.Vector3();
    const dir = new THREE.Vector3();
    controller.getWorldPosition(origin);
    dir.set(0, 0, -1).applyQuaternion(controller.getWorldQuaternion(new THREE.Quaternion()));
    const ray = new THREE.Raycaster(origin, dir);

    const meshes: THREE.Object3D[] = [];
    const meshToPart = new Map<THREE.Object3D, PlacedPart>();
    for (const p of placedParts) {
      p.object.traverse((node) => {
        const m = node as THREE.Mesh;
        if (m.isMesh) { meshes.push(m); meshToPart.set(m, p); }
      });
    }
    const hits = ray.intersectObjects(meshes, false);
    if (hits.length === 0) return null;
    return meshToPart.get(hits[0].object) ?? null;
  }

  function pollStickClick(controllers: THREE.Object3D[]): void {
    let i = 0;
    for (const src of session.inputSources) {
      const ctrl = controllers[i];
      i++;
      if (!ctrl) continue;
      const gp = src.gamepad;
      // button[3] = thumbstick click
      if (!gp || gp.buttons.length < 4) continue;
      const isDown = gp.buttons[3]?.pressed ?? false;
      const wasDown = prevStickClick.get(src) ?? false;
      if (isDown && !wasDown) {
        const part = findPointedPart(ctrl);
        if (part) toggleInfoPanel(part);
      }
      prevStickClick.set(src, isDown);
    }
    if (prevStickClick.size > 4) {
      const live = new Set(session.inputSources);
      for (const k of [...prevStickClick.keys()]) if (!live.has(k)) prevStickClick.delete(k);
    }
  }

  // ── Physics ──────────────────────────────────────────────────────────
  function togglePhysics(part: PlacedPart): void {
    if (part.physicsEnabled) disablePhysics(part);
    else enablePhysics(part);
    if (part.infoPanel) {
      part.infoPanel.setButtonLabel(0, part.physicsEnabled ? "Disable Physics" : "Enable Physics");
    }
    saveState();
  }

  function enablePhysics(part: PlacedPart): void {
    if (part.body) return;
    const body = makePhysicsBody(part, partMat);
    if (!body) return;
    world.addBody(body);
    part.body = body;
    part.physicsEnabled = true;
    // Anchor sync would fight gravity; suppress until physics is disabled.
    // We don't delete the anchor — keeping it lets us "freeze" the part back
    // at the original spot if user disables physics quickly.
  }

  function disablePhysics(part: PlacedPart): void {
    if (!part.body) {
      part.physicsEnabled = false;
      return;
    }
    // Capture current world position/orientation so the part stays where it
    // came to rest, then drop the body.
    const finalPos = new THREE.Vector3();
    part.object.getWorldPosition(finalPos);
    world.removeBody(part.body);
    part.body = null;
    part.bodyLocalOffset = null;
    part.physicsEnabled = false;
    // Re-anchor at the new position so persistence reflects where it landed.
    if (part.anchor) {
      try { (part.anchor as PersistableAnchor).delete?.(); } catch { /* ignore */ }
      part.anchor = null;
    }
    if (part.anchorHandle) {
      const handle = part.anchorHandle;
      part.anchorHandle = null;
      try {
        void (session as AnchorCapableSession).deletePersistentAnchor?.(handle);
      } catch { /* ignore */ }
    }
    part.needsAnchor = true;
  }

  function makePhysicsBody(part: PlacedPart, mat: CANNON.Material): CANNON.Body | null {
    const localBox = new THREE.Box3();
    let hasGeometry = false;
    part.object.traverse((node) => {
      const m = node as THREE.Mesh;
      if (!m.isMesh || !m.geometry) return;
      if (!m.geometry.boundingBox) m.geometry.computeBoundingBox();
      if (m.geometry.boundingBox) {
        localBox.union(m.geometry.boundingBox);
        hasGeometry = true;
      }
    });
    if (!hasGeometry) return null;

    const localSize = new THREE.Vector3();
    const localCenter = new THREE.Vector3();
    localBox.getSize(localSize);
    localBox.getCenter(localCenter);

    const scale = part.object.scale.x; // uniform from autoScaleAndCenter
    const worldHalf = new CANNON.Vec3(
      (localSize.x * scale) / 2,
      (localSize.y * scale) / 2,
      (localSize.z * scale) / 2,
    );

    // Body sits at the AABB center in world space, rotated with the mesh.
    const offsetLocal = localCenter.clone().multiplyScalar(scale);
    const offsetWorld = offsetLocal.clone().applyQuaternion(part.object.quaternion);
    const partWorld = new THREE.Vector3();
    part.object.getWorldPosition(partWorld);
    const bodyPos = partWorld.add(offsetWorld);

    part.bodyLocalOffset = offsetLocal;

    return new CANNON.Body({
      mass: PART_MASS,
      material: mat,
      shape: new CANNON.Box(worldHalf),
      position: new CANNON.Vec3(bodyPos.x, bodyPos.y, bodyPos.z),
      quaternion: new CANNON.Quaternion(
        part.object.quaternion.x,
        part.object.quaternion.y,
        part.object.quaternion.z,
        part.object.quaternion.w,
      ),
      angularDamping: 0.2,
      linearDamping: 0.05,
    });
  }

  function syncPhysicsToMeshes(): void {
    for (const p of placedParts) {
      if (!p.physicsEnabled || !p.body || !p.bodyLocalOffset) continue;
      // mesh.quaternion = body.quaternion
      p.object.quaternion.set(
        p.body.quaternion.x, p.body.quaternion.y, p.body.quaternion.z, p.body.quaternion.w,
      );
      // mesh.position = body.position - body.quaternion * bodyLocalOffset
      const offset = p.bodyLocalOffset.clone().applyQuaternion(p.object.quaternion);
      p.object.position.set(
        p.body.position.x - offset.x,
        p.body.position.y - offset.y,
        p.body.position.z - offset.z,
      );
    }
  }

  // ── Grab / release ──────────────────────────────────────────────────
  function tryGrab(controller: THREE.Object3D): boolean {
    let best: PlacedPart | null = null;

    // Close-range grab first — nearest part within GRAB_RANGE wins.
    let bestDist = GRAB_RANGE;
    const ctrlPos = new THREE.Vector3();
    controller.getWorldPosition(ctrlPos);
    for (const p of placedParts) {
      if ([...grabbed.values()].includes(p)) continue;
      const partPos = new THREE.Vector3();
      p.object.getWorldPosition(partPos);
      const d = ctrlPos.distanceTo(partPos);
      if (d < bestDist) { best = p; bestDist = d; }
    }

    // Remote grab via the pointer beam — pick whatever the controller is
    // aimed at, regardless of distance. Lets the user fish parts off shelves
    // or out of corners they can't physically reach.
    if (!best) {
      const pointed = findPointedPart(controller);
      if (pointed && ![...grabbed.values()].includes(pointed)) {
        best = pointed;
      }
    }

    if (!best) return false;
    // If physics is on, drop the body for the duration of the grab so we can
    // drive the part directly without fighting the simulation.
    if (best.body) {
      world.removeBody(best.body);
      best.body = null;
      best.bodyLocalOffset = null;
    }
    controller.attach(best.object);
    grabbed.set(controller, best);
    setLastFocused(best);
    return true;
  }

  function releaseGrabbed(controller: THREE.Object3D): void {
    const part = grabbed.get(controller);
    if (!part) return;
    scene.attach(part.object);
    group.attach(part.object);
    grabbed.delete(controller);
    // Drop the old anchor — it was at the part's previous position. The render
    // loop will create a new anchor at the current position next frame.
    if (part.anchor) {
      try { (part.anchor as PersistableAnchor).delete?.(); } catch { /* ignore */ }
      part.anchor = null;
    }
    if (part.anchorHandle) {
      const handle = part.anchorHandle;
      part.anchorHandle = null;
      try {
        void (session as AnchorCapableSession).deletePersistentAnchor?.(handle);
      } catch { /* ignore */ }
    }
    part.needsAnchor = true;
    // If physics was enabled before the grab, re-create the body at the new
    // pose so the part keeps simulating.
    if (part.physicsEnabled && !part.body) {
      const body = makePhysicsBody(part, partMat);
      if (body) { world.addBody(body); part.body = body; }
    }
    saveState();
  }

  // ── Delete ──────────────────────────────────────────────────────────
  async function deletePart(part: PlacedPart): Promise<void> {
    const idx = placedParts.indexOf(part);
    if (idx >= 0) placedParts.splice(idx, 1);
    if (lastFocused === part) setLastFocused(placedParts[placedParts.length - 1] ?? null);
    // Pickers may be referenced by hover state; clear before disposing.
    for (const [ctrl, hov] of pickerHover) {
      if (hov?.part === part) pickerHover.set(ctrl, null);
    }
    if (part.body) {
      world.removeBody(part.body);
      part.body = null;
    }
    group.remove(part.object); disposeObject3D(part.object);
    group.remove(part.picker); disposeObject3D(part.picker);
    if (part.infoPanel) {
      group.remove(part.infoPanel.group);
      disposeObject3D(part.infoPanel.group);
      part.infoPanel = null;
    }
    if (part.anchor) {
      try { (part.anchor as PersistableAnchor).delete?.(); } catch { /* ignore */ }
    }
    if (part.anchorHandle) {
      try {
        await (session as AnchorCapableSession).deletePersistentAnchor?.(part.anchorHandle);
      } catch { /* ignore */ }
    }
    saveState();
    refreshStatus();
  }

  // ── Joystick rotation (acts on lastFocused) ─────────────────────────
  // Rotates around camera-relative axes computed each frame, so "stick right"
  // always rotates the part right from the user's POV regardless of where
  // they're standing. World-axis rotation made pitch feel disconnected from
  // input after yawing the part.
  const tmpQuat = new THREE.Quaternion();
  const accumQuat = new THREE.Quaternion();
  const camQuat = new THREE.Quaternion();
  const camRight = new THREE.Vector3();
  const camUp = new THREE.Vector3();
  const camForward = new THREE.Vector3();

  function applyStickRotation(dt: number): void {
    if (!lastFocused) return;
    if (!placedParts.includes(lastFocused)) { setLastFocused(null); return; }
    if (grabbed.size > 0) return;
    // Joystick rotation would fight the physics simulation — skip it when
    // physics is on for the focused part. (User can disable physics first if
    // they want to nudge the rotation.)
    if (lastFocused.physicsEnabled) return;

    const left = readStick(session, "left");
    const right = readStick(session, "right");

    const yaw   = -applyDeadzone(left[0])  * ROT_SENSITIVITY * dt;
    const pitch = -applyDeadzone(left[1])  * ROT_SENSITIVITY * dt;
    const roll  = -applyDeadzone(right[0]) * ROT_SENSITIVITY * dt;

    if (yaw === 0 && pitch === 0 && roll === 0) return;

    camera.getWorldQuaternion(camQuat);
    camRight.set(1, 0, 0).applyQuaternion(camQuat);
    camUp.set(0, 1, 0).applyQuaternion(camQuat);
    camForward.set(0, 0, -1).applyQuaternion(camQuat);

    accumQuat.identity();
    if (yaw !== 0)   { tmpQuat.setFromAxisAngle(camUp, yaw);       accumQuat.premultiply(tmpQuat); }
    if (pitch !== 0) { tmpQuat.setFromAxisAngle(camRight, pitch);  accumQuat.premultiply(tmpQuat); }
    if (roll !== 0)  { tmpQuat.setFromAxisAngle(camForward, roll); accumQuat.premultiply(tmpQuat); }
    lastFocused.object.quaternion.premultiply(accumQuat);

    // Rotation is stored in localStorage (not in the anchor), so we don't
    // need to invalidate the anchor. Just save the new orientation.
    scheduleSave();
  }

  // ── Anchors ─────────────────────────────────────────────────────────
  function maintainAnchors(frame: XRFrame, refSpace: XRReferenceSpace): void {
    for (const p of placedParts) {
      const isGrabbed = [...grabbed.values()].includes(p);
      // Physics-driven parts are positioned by syncPhysicsToMeshes; skip anchor.
      if (p.physicsEnabled) continue;
      if (p.anchor && !isGrabbed) {
        const pose = frame.getPose(p.anchor.anchorSpace, refSpace);
        if (pose) {
          // Position from anchor (drift-corrected by the runtime), but
          // preserve rotation set by joystick / load.
          p.object.position.set(pose.transform.position.x, pose.transform.position.y, pose.transform.position.z);
        }
      }
      if (p.needsAnchor && !p.anchorPending && !isGrabbed) {
        void createAnchorForPart(p, frame, refSpace);
      }
    }
  }

  async function createAnchorForPart(
    part: PlacedPart,
    frame: XRFrame,
    refSpace: XRReferenceSpace,
  ): Promise<void> {
    const f = frame as AnchorCapableFrame;
    if (typeof f.createAnchor !== "function") {
      // Anchors not supported on this device; give up gracefully.
      part.needsAnchor = false;
      return;
    }
    part.anchorPending = true;
    const pos = new THREE.Vector3();
    part.object.getWorldPosition(pos);
    // Anchor stores position only. We use identity orientation; rotation lives
    // in the part's quaternion + localStorage.
    const transform = new XRRigidTransform(
      { x: pos.x, y: pos.y, z: pos.z, w: 1 },
      { x: 0, y: 0, z: 0, w: 1 },
    );
    try {
      const anchor = await f.createAnchor(transform, refSpace);
      part.anchor = anchor;
      part.needsAnchor = false;
      const pAnchor = anchor as PersistableAnchor;
      if (typeof pAnchor.requestPersistentHandle === "function") {
        try {
          part.anchorHandle = await pAnchor.requestPersistentHandle();
          saveState();
        } catch (err) {
          console.warn("requestPersistentHandle failed:", err);
        }
      }
    } catch (err) {
      console.warn("createAnchor failed:", err);
    } finally {
      part.anchorPending = false;
    }
  }

  // ── Persistence ─────────────────────────────────────────────────────
  function saveState(): void {
    const data: { version: number; parts: PersistedPart[] } = {
      version: 1,
      parts: placedParts
        .filter((p) => p.anchorHandle)
        .map((p) => ({
          filename: p.filename,
          color: p.color,
          anchorHandle: p.anchorHandle!,
          rotation: [
            p.object.quaternion.x,
            p.object.quaternion.y,
            p.object.quaternion.z,
            p.object.quaternion.w,
          ] as [number, number, number, number],
          physicsEnabled: p.physicsEnabled,
        })),
    };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch { /* full disk etc. */ }
  }

  async function restoreFromStorage(): Promise<void> {
    let raw: string | null = null;
    try { raw = localStorage.getItem(STORAGE_KEY); } catch { return; }
    if (!raw) return;
    let data: { version: number; parts: PersistedPart[] } | null = null;
    try { data = JSON.parse(raw); } catch { return; }
    if (!data || !Array.isArray(data.parts) || data.parts.length === 0) return;

    const sessionWith = session as AnchorCapableSession;
    if (typeof sessionWith.restorePersistentAnchor !== "function") {
      // Device doesn't support persistent anchors. Drop the saved state to
      // avoid confusing the user with phantom entries.
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      return;
    }

    setStatus(`Restoring ${data.parts.length} placed part${data.parts.length === 1 ? "" : "s"}…`);
    let restored = 0; let dropped = 0;
    for (const entry of data.parts) {
      try {
        const anchor = await sessionWith.restorePersistentAnchor(entry.anchorHandle);
        await loadAndPlace(
          { name: entry.filename, size: 0, mtime: 0 },
          {
            restoreColor: entry.color,
            restoreAnchor: anchor,
            restoreHandle: entry.anchorHandle,
            restoreRotation: entry.rotation,
            restorePhysicsEnabled: entry.physicsEnabled === true,
          },
        );
        restored++;
      } catch (err) {
        console.warn(`Restore failed for ${entry.filename}:`, err);
        dropped++;
      }
    }
    if (dropped > 0) saveState(); // prune the failed entries from storage
    if (restored > 0) refreshStatus();
  }

  // ── Init ────────────────────────────────────────────────────────────
  await restoreFromStorage();
  void showList();

  return {
    group,
    update(dt, controllersArg, frame, refSpace) {
      applyStickRotation(dt);
      pollPickerToggle();
      pollGripButton();
      pollStickClick(controllersArg);
      updatePickerVisibility();
      if (listPanel) {
        for (const ctrl of controllersArg) listPanel.updateHover(ctrl);
      }
      if (pickersVisible) {
        updatePickerPlacements();
        updatePickerHover(controllersArg);
      }
      updateInfoPanels(controllersArg);
      // Physics sync uses LAST frame's body state (world.step happens later
      // in main's render loop). One-frame lag, invisible at 90 Hz.
      syncPhysicsToMeshes();
      if (frame && refSpace) maintainAnchors(frame, refSpace);
    },
    listHoverDistance(controller) {
      // Closest hit across list panel, info panels, and per-part picker.
      let best: number | null = null;
      if (listPanel) {
        const d = listPanel.hoverDistance(controller);
        if (d !== null) best = d;
      }
      const info = infoPanelHoverDistance(controller);
      if (info !== null && (best === null || info < best)) best = info;
      const pick = pickerHoverDistance(controller);
      if (pick !== null && (best === null || pick < best)) best = pick;
      return best;
    },
    onSelectStart(controller) {
      if (listPanel) {
        const button = listPanel.updateHover(controller);
        if (button) { button.onSelect(); return true; }
      }
      if (tryInfoPanelClick(controller)) return true;
      // Color or delete — picker action takes priority over grab.
      if (tryPickerAction(controller)) return true;
      if (tryGrab(controller)) return true;
      return false;
    },
    onSelectEnd(controller) {
      releaseGrabbed(controller);
    },
    async refresh() { await showList(); },
    tryDismissOverlay() {
      // Only consume the back press if there's something to fall back to —
      // i.e. parts are already placed in the scene. Otherwise B goes straight
      // back to the main panel so the user isn't stuck with an empty view.
      if (listPanel && placedParts.length > 0) {
        group.remove(listPanel.group);
        listPanel = null;
        refreshStatus();
        return true;
      }
      return false;
    },
    dispose() {
      if (saveTimer !== null) { window.clearTimeout(saveTimer); saveTimer = null; saveState(); }
      for (const [, part] of grabbed) group.attach(part.object);
      grabbed.clear();
      if (listPanel) {
        group.remove(listPanel.group);
        listPanel = null;
      }
      // Note: we do NOT delete persistent anchors here. They survive intentionally
      // so the next session can restore. We do dispose Three.js resources and
      // remove physics bodies from the world.
      for (const p of placedParts) {
        if (p.body) { world.removeBody(p.body); p.body = null; }
        disposeObject3D(p.object);
        disposeObject3D(p.picker);
        if (p.infoPanel) disposeObject3D(p.infoPanel.group);
      }
      placedParts.length = 0;
      pickerHover.clear();
      prevStickClick.clear();
      scene.remove(group);
    },
  };
}

// ── Module-scope helpers ────────────────────────────────────────────────
async function loadPart(
  name: string,
  stlLoader: STLLoader,
  mfLoader: ThreeMFLoader,
): Promise<THREE.Object3D> {
  const ext = name.toLowerCase().split(".").pop();
  const url = partUrl(name);

  if (ext === "stl") {
    const buffer = await fetch(url).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.arrayBuffer();
    });
    const geom = stlLoader.parse(buffer);
    if (!geom.attributes.normal) geom.computeVertexNormals();
    geom.computeBoundingBox();
    const mat = new THREE.MeshStandardMaterial({
      color: 0xc9c9d2, roughness: 0.55, metalness: 0.05,
    });
    return new THREE.Mesh(geom, mat);
  }

  if (ext === "3mf") {
    return new Promise((resolve, reject) => {
      mfLoader.load(
        url,
        (g) => resolve(g),
        undefined,
        (err) => reject(err instanceof Error ? err : new Error(String(err))),
      );
    });
  }

  throw new Error(`unsupported extension: ${ext ?? "(none)"}`);
}

function autoScaleAndCenter(obj: THREE.Object3D, targetMaxSize: number): void {
  const box = new THREE.Box3().setFromObject(obj);
  const size = new THREE.Vector3();
  const center = new THREE.Vector3();
  box.getSize(size);
  box.getCenter(center);
  const maxDim = Math.max(size.x, size.y, size.z);
  if (maxDim > 0) {
    const s = targetMaxSize / maxDim;
    obj.scale.setScalar(s);
    obj.position.sub(center.multiplyScalar(s));
  }
}

function disposeObject3D(obj: THREE.Object3D): void {
  obj.traverse((node) => {
    const m = node as THREE.Mesh;
    if (m.geometry) m.geometry.dispose();
    const mat = m.material;
    if (Array.isArray(mat)) mat.forEach((mm) => mm.dispose());
    else if (mat) mat.dispose();
  });
  if (obj.parent) obj.parent.remove(obj);
}

function applyColor(obj: THREE.Object3D, color: number): void {
  obj.traverse((node) => {
    const m = node as THREE.Mesh;
    if (!m.material) return;
    const mats = Array.isArray(m.material) ? m.material : [m.material];
    for (const mat of mats) {
      const c = (mat as { color?: THREE.Color }).color;
      if (c) c.setHex(color);
    }
  });
}

function readStick(session: XRSession, hand: "left" | "right"): [number, number] {
  for (const src of session.inputSources) {
    if (src.handedness !== hand) continue;
    const gp = src.gamepad;
    if (!gp || gp.axes.length < 4) return [0, 0];
    return [gp.axes[2] ?? 0, gp.axes[3] ?? 0];
  }
  return [0, 0];
}

function applyDeadzone(v: number): number {
  if (Math.abs(v) < STICK_DEADZONE) return 0;
  return Math.sign(v) * (Math.abs(v) - STICK_DEADZONE) / (1 - STICK_DEADZONE);
}

function trimLabel(name: string): string {
  if (name.length <= 22) return name;
  const ext = name.includes(".") ? "." + name.split(".").pop() : "";
  return name.slice(0, 18) + "…" + ext;
}
